/**
 * <welloura-marquee> — seamless infinite scroll strip.
 *
 * Renders its content once on the server (real, accessible markup), then
 * clones it client-side to fill the viewport and duplicates the result once
 * more so a CSS translateX(-50%) loop is always seamless, regardless of how
 * many items or how wide the container is. Clones are marked aria-hidden.
 */
class WellouraMarquee extends HTMLElement {
  connectedCallback() {
    this.wrapper = this.querySelector('[data-wl-marquee-wrapper]');
    this.content = this.querySelector('[data-wl-marquee-content]');

    if (!this.wrapper || !this.content || !this.content.children.length) return;

    this.originalHTML = this.content.innerHTML;
    this.speedFactor = Number(this.getAttribute('data-speed-factor')) || 6;

    this.build();

    this.handleResize = this.debounce(() => this.build(), 200);
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
  }

  debounce(fn, wait) {
    let timer;
    return () => {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  build() {
    this.classList.remove('is-ready');
    this.content.innerHTML = this.originalHTML;
    this.wrapper.querySelectorAll('[data-wl-marquee-clone]').forEach((el) => el.remove());

    const items = Array.from(this.content.children);
    if (!items.length) return;

    const itemsWidth = this.content.scrollWidth || 1;
    const containerWidth = this.offsetWidth || itemsWidth;
    const copies = Math.max(1, Math.ceil(containerWidth / itemsWidth));

    for (let i = 1; i < copies; i++) {
      items.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        this.content.appendChild(clone);
      });
    }

    const fullClone = this.content.cloneNode(true);
    fullClone.setAttribute('aria-hidden', 'true');
    fullClone.setAttribute('data-wl-marquee-clone', '');
    fullClone.removeAttribute('data-wl-marquee-content');
    this.wrapper.appendChild(fullClone);

    this.style.setProperty('--wl-marquee-duration', copies * this.speedFactor + 's');
    this.classList.add('is-ready');
  }
}

if (!customElements.get('welloura-marquee')) {
  customElements.define('welloura-marquee', WellouraMarquee);
}
